/**
 * Utility functions for age verification form
 */

export interface DateOption {
  value: string;
  label: string;
}

/**
 * Generate months array for dropdown
 */
export function generateMonths(): DateOption[] {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return months.map((month, index) => ({
    value: (index + 1).toString().padStart(2, '0'),
    label: month,
  }));
}

/**
 * Generate days array for dropdown (1-31)
 */
export function generateDays(): DateOption[] {
  const days: DateOption[] = [];

  for (let day = 1; day <= 31; day++) {
    days.push({
      value: day.toString().padStart(2, '0'),
      label: day.toString(),
    });
  }

  return days;
}

/**
 * Generate years array for dropdown
 * Only includes years that are 18 years prior up to the last 100 years
 */
export function generateYears(): DateOption[] {
  const currentYear = new Date().getFullYear();
  const minAge = 18;
  const maxAge = 100;

  const oldestYear = currentYear - minAge;
  const youngestYear = currentYear - maxAge;

  const years: DateOption[] = [];

  for (let year = oldestYear; year >= youngestYear; year--) {
    years.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return years;
}

/**
 * Validate if all form fields are filled
 */
export function validateAgeVerificationForm(formData: {
  province: string;
  month: string;
  day: string;
  year: string;
}): {
  isValid: boolean;
  fieldErrors: {
    province?: string;
    dateOfBirth?: string;
  };
} {
  const fieldErrors: { province?: string; dateOfBirth?: string } = {};

  if (!formData.province) {
    fieldErrors.province = 'Province is required';
  }

  if (!formData.month || !formData.day || !formData.year) {
    fieldErrors.dateOfBirth = 'Date of birth is required';
  } else if (!isValidDate(formData.year, formData.month, formData.day)) {
    fieldErrors.dateOfBirth = 'Invalid date of birth';
  }

  return {
    isValid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
  };
}

/**
 * Check if the selected date is valid (e.g., February 30th doesn't exist)
 */
export function isValidDate(year: string, month: string, day: string): boolean {
  if (!year || !month || !day) return false;

  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  return (
    date.getFullYear() === parseInt(year) &&
    date.getMonth() === parseInt(month) - 1 &&
    date.getDate() === parseInt(day)
  );
}
